/*
 * One matter fetch per case, shared by the shell (which needs the client id to
 * canonicalise the URL) and the header (assignees, linked client).
 */
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { MattersAPI } from "../../../api/matters";
import type { MatterDTO } from "../../../api/types/matter";

export type MatterState = {
  matter: MatterDTO | null;
  isLoading: boolean;
  /** Set once the fetch has settled, whether or not it succeeded. */
  settled: boolean;
  error: unknown;
  refetch: () => Promise<void>;
  /** Client id the matter is linked to, or null when unlinked / unknown. */
  foreignNationalId: string | null;
};

const MatterContext = createContext<MatterState | null>(null);

export const MatterProvider: FC<{
  individualId: string;
  children: ReactNode;
}> = ({ individualId, children }) => {
  const [matter, setMatter] = useState<MatterDTO | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(individualId));
  const [settled, setSettled] = useState(!individualId);
  const [error, setError] = useState<unknown>(null);
  /** The case the state above belongs to; the provider stays mounted across case switches. */
  const [stateFor, setStateFor] = useState(individualId);

  const load = useCallback(async () => {
    if (!individualId) return;
    setIsLoading(true);
    try {
      const next = await MattersAPI.get(individualId);
      setMatter(next);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      setSettled(true);
    }
  }, [individualId]);

  useEffect(() => {
    let cancelled = false;
    setMatter(null);
    setError(null);
    setSettled(!individualId);
    setStateFor(individualId);
    if (!individualId) {
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    MattersAPI.get(individualId)
      .then((next) => {
        if (!cancelled) setMatter(next);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setSettled(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [individualId]);

  // Until the effect resets for a new case, the previous case's matter must
  // not leak out (it would rehome the URL under the wrong client).
  const current = stateFor === individualId;
  const value = useMemo<MatterState>(() => {
    const m = current ? matter : null;
    return {
      matter: m,
      isLoading: current ? isLoading : Boolean(individualId),
      settled: current ? settled : !individualId,
      error: current ? error : null,
      refetch: load,
      foreignNationalId:
        (m?.individual?.foreign_national_id ?? "").trim() || null
    };
  }, [current, error, individualId, isLoading, load, matter, settled]);

  return (
    <MatterContext.Provider value={value}>{children}</MatterContext.Provider>
  );
};

export const useMatterOptional = (): MatterState | null =>
  useContext(MatterContext);

export const useMatter = (): MatterState => {
  const ctx = useContext(MatterContext);
  if (!ctx) throw new Error("useMatter must be used within MatterProvider");
  return ctx;
};
